import { MapPin, CreditCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button } from '~/components/ui';
import type { RootState } from '~/redux/store';
import { formatCurrency } from '~/utils';

interface OrderSummaryProps {
  selectedAddressId: string;
  onAddressChange: (addressId: string) => void;
  subtotal: number;
  itemCount: number;
  shippingFee: string;
  total: number;
  onCheckout: () => void;
  isPlaceOrderDisabled: boolean;
}

export function OrderSummary({
  selectedAddressId,
  onAddressChange,
  subtotal,
  itemCount,
  shippingFee,
  total,
  onCheckout,
  isPlaceOrderDisabled,
}: OrderSummaryProps) {
  const user = useSelector((state: RootState) => state.auth.user);
  const locations = user?.companies?.map((company) => company.locations).flat();
  const selectedAddress = locations?.find(
    (loc) => loc.id === selectedAddressId
  );

  const { t } = useTranslation('cart');

  return (
    <div className="lg:col-span-1 border-t lg:border-t-0 lg:border-l border-neut-100 bg-neut-50/50 p-4 sm:p-6 flex flex-col gap-6">
      {/* Address Selection */}
      <div>
        <label className="flex items-center gap-2 text-sm text-neut-700 mb-3 font-medium">
          <MapPin className="w-4 h-4 text-prim-500" />
          {t('cart.shipping_address')}
        </label>
        <select
          data-testid="shipping-address-select"
          name="shippingAddressSelect"
          value={selectedAddressId}
          onChange={(e) => onAddressChange(e.target.value)}
          className="w-full px-3 py-2.5 text-sm border border-neut-border rounded-lg focus:outline-none focus:ring-2 focus:ring-prim-500 font-normal bg-white"
        >
          <option value="">{t('cart.select_address')}</option>
          {locations?.map((address) => (
            <option key={address.id} value={address.id}>
              {address.name}
            </option>
          ))}
        </select>
      </div>
      {selectedAddressId ? (
        <div className="p-3 bg-neut-0 rounded-lg border border-neut-border">
          <p className="text-sm text-neut-sec">
            {selectedAddress?.addressLine1}
          </p>
          <p className="text-sm text-neut-sec mt-1">
            {selectedAddress?.city}, {selectedAddress?.region}{' '}
            {selectedAddress?.zipCode}
          </p>
        </div>
      ) : null}

      {/* Order Summary */}
      <div className="flex-1 flex flex-col">
        <h4 className="text-sm text-neut-prim font-semibold mb-3">
          {t('cart.order_summary')}
        </h4>

        <div className="space-y-2.5 mb-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-neut-sec font-normal">
              {t('cart.subtotal', { itemsCount: itemCount })}
            </span>
            <span className="text-neut-prim font-medium">
              {formatCurrency(subtotal)}
            </span>
          </div>

          <div className="flex items-center justify-between text-sm">
            <span className="text-neut-sec font-normal">
              {t('cart.shipping_fee')}
            </span>
            <span className="text-neut-prim font-medium">{shippingFee}</span>
          </div>

          <div className="border-t border-neut-300 pt-2.5 flex items-center justify-between">
            <span className="text-neut-prim font-semibold">
              {t('cart.total')}
            </span>
            <span className="text-xl text-neut-prim font-semibold">
              {formatCurrency(total)}
            </span>
          </div>
        </div>

        {/* Place Order Button */}
        <div className="mt-auto">
          <Button onClick={onCheckout} disabled={isPlaceOrderDisabled}>
            <span className="flex items-center justify-center gap-2">
              <CreditCard className="w-4 h-4" />
              {t('cart.checkout')}
            </span>
          </Button>

          {isPlaceOrderDisabled && (
            <p className="text-xs text-warning-600 text-center mt-2">
              ⚠️ {t('cart.address_prompt')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
